import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ChronicDiagnosticData } from '@models/medical-history.model';
import { AlertService } from '@services/alert.service';
import { MedicalHistoryService } from '@services/medical-history.service';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'gc-chronic',
  templateUrl: './chronic.component.html',
  styleUrls: ['./chronic.component.scss'],
  standalone: false,
})
export class ChronicComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userId: number;
  @Input() chronicData: ChronicDiagnosticData[];
  @Input() requestUuid: string | null = null;
  @Output() submittedStep = new EventEmitter<{ step: string; values: any }>();

  subscriptions: Subscription[] = [];
  chronicForm: FormGroup;
  newChronicICD10Form = this.fb.group({
    diagnosis: ['', Validators.required],
    note: '',
    date: [DateTime.now().toISODate()],
  });
  newChronicMedicationForm = this.fb.group({
    diagnosis: [''],
    medication: ['', Validators.required],
    note: '',
    date: [DateTime.now().toISODate()],
  });
  constructor(
    private readonly fb: FormBuilder,
    private readonly medicalHistoryService: MedicalHistoryService,
    private readonly alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.buildChronicForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chronicData'] && !changes['chronicData'].firstChange) {
      this.buildChronicForm();
    }
  }

  buildChronicForm() {
    if (!this.chronicData) {
      this.chronicForm = this.fb.group({
        diagnosis: this.fb.array([]),
        medication: this.fb.array([]),
      });
      return;
    }
    const medicationList = [];
    const diagnosisList = [];
    this.chronicData?.forEach((item) => {
      if (item.default_lines) {
        medicationList.push({
          date: item.date,
          note: item.note,
          medication: item.default_lines[0]?.description,
          diagnosis: item.default_lines[0]?.diagnosis ? item.default_lines[0]?.diagnosis[0].description : '',
        });
      } else {
        diagnosisList.push({
          date: item.date,
          note: item.note,
          diagnosis: item.diagnosis[0].description,
        });
      }
    });

    this.chronicForm = this.fb.group({
      diagnosis: this.fb.array(diagnosisList?.map((diagnosis) => this.fb.group(diagnosis))) || this.fb.array([]),
      medication: this.fb.array(medicationList.map((medication) => this.fb.group(medication))) || this.fb.array([]),
    });
  }

  checkDateValid(control: AbstractControl): boolean {
    if (control.touched && control.value === '') {
      return true;
    }
    return false;
  }

  comparisonFn(diagnosis1: string, diagnosis2: string) {
    return diagnosis1 === diagnosis2;
  }

  addChronicItem(chronicField: string) {
    const newChronicForm = this.fb.group({
      date: [DateTime.now().toISODate()],
      diagnosis: ['', null],
      note: ['', null],
      medication: ['', null],
    });
    if (chronicField === 'diagnosis' && !this.newChronicICD10Form.invalid) {
      const newChronicItem = this.newChronicICD10Form.value;
      this.newChronicICD10Form.reset();
      newChronicForm.patchValue({ diagnosis: newChronicItem.diagnosis, date: newChronicItem.date, note: newChronicItem.note });
    } else {
      const newChronicItem = this.newChronicMedicationForm.value;
      this.newChronicMedicationForm.reset();
      newChronicForm.addControl('medication', this.fb.control(newChronicItem.medication, Validators.required));
      newChronicForm.patchValue({
        diagnosis: newChronicItem.diagnosis,
        date: newChronicItem.date,
        note: newChronicItem.note,
        medication: newChronicItem.medication,
      });
    }
    (this.chronicForm.get(chronicField) as FormArray).push(newChronicForm);
  }

  get selectedChronicConditionsList() {
    return this.chronicForm.get('diagnosis').value.map((item) => item.diagnosis);
  }

  get chronicConditionsList(): FormArray {
    return this.chronicForm.get('diagnosis') as FormArray;
  }

  get chronicMedicationsList(): FormArray {
    return this.chronicForm.get('medication') as FormArray;
  }

  remove(type, i) {
    (this.chronicForm.get(type) as FormArray).removeAt(i);
  }

  saveToRequest(chronicPatientReport) {
    this.medicalHistoryService
      .saveRequestMedicalHistory$(this.requestUuid, 'chronic_items', chronicPatientReport)
      .pipe(take(1))
      .subscribe((request) => {
        if (request) {
          this.submittedStep.emit({
            step: 'chronicItems',
            values: chronicPatientReport,
          });
          this.alertService.snackbar('chronic_conditions_saved');
        }
      });
  }

  saveToProfile(chronicPatientReport) {
    this.medicalHistoryService
      .saveMedicalHistory$(this.userId, 'chronic_items', { user_id: this.userId, chronic_items: chronicPatientReport })
      .pipe(take(1))
      .subscribe(() => this.alertService.snackbar('chronic_conditions_saved'));
  }

  submit() {
    const chronicPatientReportedDiagnoses = this.chronicForm.get('diagnosis').value;
    const chronicPatientReportedMedications = this.chronicForm.get('medication').value;
    const chronicItems = [];
    chronicPatientReportedDiagnoses.forEach((item) => {
      chronicItems.push({ date: item.date, note: item.note, diagnosis: [{ description: item.diagnosis }] });
    });
    chronicPatientReportedMedications.forEach((item) => {
      chronicItems.push({
        date: item.date,
        note: item.note,
        default_lines: [{ diagnosis: [{ description: item.diagnosis }], description: item.medication }],
      });
    });
    if (this.requestUuid) {
      this.saveToRequest(chronicItems);
    } else {
      this.saveToProfile(chronicItems);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
